import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, Smartphone, Monitor } from 'lucide-react';
import { Button } from '../ui/button';

export const Hero = () => {
  const trustBadges = [
    { icon: Monitor, text: 'Acesse pelo PC' },
    { icon: Smartphone, text: 'Acesse pelo Celular' },
    { icon: CheckCircle2, text: 'Teste sem cartão' },
  ];

  return (
    <section className="hero-bg min-h-screen pt-20 lg:pt-0 flex items-center relative overflow-hidden" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20 w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center lg:text-left"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#D1FAE5]/50 rounded-full mb-6"
              data-testid="hero-badge"
            >
              <span className="w-2 h-2 bg-[#047857] rounded-full animate-pulse"></span>
              <span className="text-[#065F46] font-medium text-sm">Sistema de Gestão Financeira</span>
            </motion.div>

            {/* Headline */}
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0F172A] leading-tight mb-6" data-testid="hero-headline">
              Chega de surpresas!{' '}
              <span className="text-[#047857]">Veja hoje</span> como estará o caixa do seu negócio com{' '}
              <span className="text-[#047857]">projeções 360</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg text-[#64748B] mb-8 max-w-lg mx-auto lg:mx-0" data-testid="hero-subheadline">
              Gerencie contas a pagar e a receber de múltiplas contas, 360 dias, de forma simples e fácil, no PC ou Celular.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-10">
              <motion.a
                href="https://app.dremais.com.br"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid="hero-cta-primary"
              >
                <Button className="btn-primary text-base w-full sm:w-auto">
                  Começar Agora Grátis
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </motion.a>
              <a href="#features" data-testid="hero-cta-secondary">
                <Button variant="outline" className="btn-secondary text-base w-full sm:w-auto">
                  Ver Funcionalidades
                </Button>
              </a>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-3 justify-center lg:justify-start" data-testid="trust-badges">
              {trustBadges.map((badge, index) => (
                <motion.div
                  key={badge.text}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className="trust-badge"
                >
                  <badge.icon className="w-4 h-4" />
                  <span>{badge.text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right Content - Dashboard Screenshot */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative"
            data-testid="hero-screenshot"
          >
            <div className="browser-frame screenshot-card animate-float">
              <div className="browser-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <img
                src="https://customer-assets.emergentagent.com/job_wallet-tracker-77/artifacts/elif53tk_Tela-1.png"
                alt="Dashboard DRE+ - Controle financeiro completo"
                className="screenshot-img"
                loading="eager"
              />
            </div>

            {/* Floating Stats Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.8 }}
              className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-xl border border-slate-100 hidden lg:block"
              data-testid="floating-stat-card"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#D1FAE5] flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-[#047857]" />
                </div>
                <div>
                  <p className="text-sm text-[#64748B]">Controle Total</p>
                  <p className="font-heading font-bold text-[#0F172A]">Receitas & Despesas</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
